"use client";

import { useEffect, useState } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { ChevronDown, Globe } from "lucide-react";
import { NAVIGATION_IDS } from "@/data/constants";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/contexts/LanguageContext";
import ThemeToggle from "@/components/ThemeToggle";

interface NavItem {
  id: string;
  label: string;
}

const Navigation = () => {
  const [activeSection, setActiveSection] = useState<string>("");
  const [isScrolled, setIsScrolled] = useState(false);
  const { scrollY } = useScroll();
  const isMobile = useIsMobile();
  const { language, toggleLanguage, t } = useLanguage();

  useMotionValueEvent(scrollY, "change", (latest) => {
    setIsScrolled(latest > 50);
  });

  // Update nav items based on language - Correct order: Gallery, Process, About, Contact
  const navItems: NavItem[] = [
    { id: NAVIGATION_IDS.GALLERY, label: t('navigation.gallery') },
    { id: NAVIGATION_IDS.PROCESS, label: t('navigation.process') },
    { id: NAVIGATION_IDS.ABOUT, label: t('navigation.about') },
    { id: NAVIGATION_IDS.CONTACT, label: t('navigation.contact') },
  ];

  // Observe sections for active navigation
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionHeight = section.clientHeight;
          
          if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
            setActiveSection(sections[i]?.id || '');
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navItems]);

  const handleScrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Get language name for display
  const getLanguageDisplay = (lang: string) => {
    switch(lang) {
      case 'EN': return 'EN';
      case 'RO': return 'RO';
      case 'BG': return 'BG';
      default: return lang;
    }
  };

  // Get language switch tooltip
  const getLanguageTooltip = (lang: string) => {
    switch(lang) {
      case 'EN': return t('navigation.switchToBulgarian');
      case 'RO': return t('navigation.switchToBulgarian');
      case 'BG': return t('navigation.switchToEnglish');
      default: return 'Switch language';
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 theme-transition ${
        isScrolled
          ? "bg-background/95 backdrop-blur-sm border-b border-primary"
          : "bg-transparent border-b border-transparent"
      }`}
      style={{
        backgroundColor: isScrolled ? "hsl(var(--background) / 0.95)" : "transparent",
        backdropFilter: isScrolled ? "blur(8px)" : "none",
      }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo/Brand */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="font-inter font-bold text-xl tracking-tight hover:opacity-80 transition-opacity text-foreground"
            aria-label="Scroll to top"
          >
            <span className="text-foreground">DUMITRESCU PHOTOGRAPHY</span>
          </button>

          {/* Desktop Navigation */}
          {!isMobile && (
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-6">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleScrollTo(item.id)}
                    className={`font-inter font-medium text-sm tracking-wide transition-colors relative py-2 ${
                      activeSection === item.id
                        ? "text-primary"
                        : "text-foreground hover:text-primary"
                    }`}
                    aria-label={`Scroll to ${item.label}`}
                  >
                    {item.label}
                    {activeSection === item.id && (
                      <motion.div
                        layoutId="activeIndicator"
                        className="absolute bottom-0 left-0 right-0 h-px bg-primary"
                        initial={false}
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* Language Switcher and Theme Toggle */}
              <div className="h-6 border-l border-border" />

              <div className="flex items-center space-x-4">
                <ThemeToggle />
                
                <button
                  onClick={toggleLanguage}
                  className="flex items-center space-x-2 font-inter font-medium text-sm text-foreground hover:text-primary transition-colors"
                  aria-label={getLanguageTooltip(language)}
                  title={getLanguageTooltip(language)}
                >
                  <Globe className="w-4 h-4" />
                  <span>{getLanguageDisplay(language)}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {/* Mobile Navigation */}
          {isMobile && (
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <ThemeToggle />
                
                <button
                  onClick={toggleLanguage}
                  className="font-inter font-medium text-sm text-foreground hover:text-primary transition-colors"
                  aria-label={getLanguageTooltip(language)}
                  title={getLanguageTooltip(language)}
                >
                  {getLanguageDisplay(language)}
                </button>
              </div>
              
              {/* Mobile menu */}
              <button 
                onClick={() => {
                  const menu = document.getElementById('mobile-menu');
                  if (menu) {
                    menu.classList.toggle('hidden');
                  }
                }}
                className="text-foreground hover:text-primary transition-colors"
                aria-label="Menu"
              >
                <span className="sr-only">Menu</span>
                <div className="w-6 h-px bg-current mb-1"></div>
                <div className="w-6 h-px bg-current mb-1"></div>
                <div className="w-6 h-px bg-current"></div>
              </button>
            </div>
          )}
        </div>

        {/* Mobile Menu (Hidden by default) */}
        <div id="mobile-menu" className="md:hidden hidden bg-background border-t border-border theme-transition">
          <div className="py-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  handleScrollTo(item.id);
                  // Close menu
                  const menu = document.getElementById('mobile-menu');
                  if (menu) menu.classList.add('hidden');
                }}
                className={`block w-full text-left px-4 py-3 font-inter font-medium text-sm theme-transition ${
                  activeSection === item.id
                    ? "text-primary bg-primary/10"
                    : "text-foreground hover:text-primary hover:bg-primary/5"
                }`}
              >
                {item.label}
              </button>
            ))}
            <div className="pt-4 border-t border-border mt-2">
              <div className="px-4 py-2 flex items-center justify-between">
                <span className="font-inter font-medium text-sm text-foreground">Language:</span>
                <button
                  onClick={toggleLanguage}
                  className="font-inter font-medium text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  {getLanguageDisplay(language)}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navigation;